import { MARKET } from "../data/market";
import { filterPanel } from "../data/panel";
import type { CheckResult, LoanInput, LvrBand, MarketProduct, Verdict } from "../types";

const LVR_VALUE: Record<LvrBand, number> = {
  "60": 60,
  "80": 80,
  "90": 90,
  "95": 95,
};

export function effectiveBalance(input: Pick<LoanInput, "balance" | "offsetBalance">): number {
  const parked = input.offsetBalance ?? 0;
  if (!input.balance) return 0;
  return Math.max(0, input.balance - Math.max(0, parked));
}

export function monthlyRepayment(
  balance: number,
  annualRate: number,
  years: number,
  repayment: "pi" | "io",
): number {
  const r = annualRate / 100 / 12;
  const n = Math.max(1, Math.round(years * 12));
  if (repayment === "io") return balance * r;
  if (r === 0) return balance / n;
  return (balance * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

export function lvrUsed(input: LoanInput): number {
  if (input.lvrExact != null && input.lvrExact > 0) return input.lvrExact;
  return LVR_VALUE[input.lvr];
}

export function eligibleProducts(
  input: LoanInput,
  products: MarketProduct[] = MARKET,
): MarketProduct[] {
  const lvr = lvrUsed(input);
  return filterPanel(products)
    .filter(
      (p) =>
        p.occupancy === input.occupancy &&
        p.repayment === input.repayment &&
        p.maxLvr >= lvr &&
        (p.minLvr == null || lvr > p.minLvr) &&
        (p.minLoan == null || input.balance >= p.minLoan),
    )
    .sort(
      (a, b) =>
        a.rate - b.rate || a.comparisonRate - b.comparisonRate || a.lender.localeCompare(b.lender),
    );
}

export function rateHint(rate: number): string | null {
  if (!rate) return null;
  if (rate > 0 && rate < 2) {
    return "If the statement says 6.50%, type 6.50 — not 0.065.";
  }
  if (rate < 3.5) {
    return "That is unusually low for an Australian variable in 2026. Use the interest rate, not a discount off a base.";
  }
  if (rate > 12) {
    return "That is unusually high. Use the interest rate on the statement, not the comparison rate.";
  }
  return null;
}

export function noPrintReason(input: LoanInput, products: MarketProduct[] = MARKET): string {
  if (!input.rate || !input.balance || !input.years) {
    return "Put the interest rate, loan amount and years left from your statement in the loan file.";
  }
  const occ = products.filter((p) => p.occupancy === input.occupancy);
  if (!occ.length) {
    return input.occupancy === "investment"
      ? "No cited investment print yet. Try owner-occupier to see a named rate."
      : "No cited owner-occupier print for this file.";
  }
  const rep = occ.filter((p) => p.repayment === input.repayment);
  if (!rep.length) {
    return input.repayment === "io"
      ? "No cited interest-only print for this occupancy. Try P&I."
      : "No cited P&I print for this occupancy.";
  }
  const lvr = lvrUsed(input);
  const lvrOk = rep.filter(
    (p) => p.maxLvr >= lvr && (p.minLvr == null || lvr > p.minLvr),
  );
  if (!lvrOk.length) {
    return `No cited print at ${lvr}% LVR. A lower LVR, or calculating from a valuation, can unlock a cheaper band.`;
  }
  const loanOk = lvrOk.filter((p) => p.minLoan == null || input.balance >= p.minLoan);
  if (!loanOk.length) {
    const floor = Math.min(...lvrOk.map((p) => p.minLoan ?? 0).filter(Boolean));
    return `Cited prints at this LVR start at $${floor.toLocaleString("en-AU")}.`;
  }
  return "No cited print for this occupancy, repayment and LVR.";
}

function verdictFor(gap: number): Verdict {
  if (gap < 0.15) return "competitive";
  if (gap < 0.4) return "review";
  return "overpaying";
}

export function runCheck(
  input: LoanInput,
  products: MarketProduct[] = MARKET,
): CheckResult | null {
  if (!input.rate || !input.balance || !input.years) return null;
  const matches = eligibleProducts(input, products);
  if (!matches.length) return null;
  const best = matches[0];
  const currentMonthly = monthlyRepayment(
    effectiveBalance(input),
    input.rate,
    input.years,
    input.repayment,
  );
  const bestMonthly = monthlyRepayment(input.balance, best.rate, input.years, input.repayment);
  const monthlySave = currentMonthly - bestMonthly;
  const gap = input.rate - best.rate;
  const discharge = 395;
  const application = best.ongoingFee > 0 ? 0 : 350;
  const switchingCost = Math.max(0, discharge + application - best.cashback);
  const breakEvenMonths =
    monthlySave > 10 ? Math.ceil(switchingCost / monthlySave) : monthlySave > 0 ? 1 : null;

  return {
    currentMonthly,
    bestMonthly,
    monthlySave,
    yearlySave: monthlySave * 12,
    gap,
    verdict: verdictFor(gap),
    best,
    comparables: citedPrints(matches, best, 3),
    eligible: matches.slice(0, 8),
    breakEvenMonths,
    switchingCost,
  };
}

/** Cheapest unique lenders from eligible prints. Not a favourite trio. Cite, do not invent. */
export function citedPrints(
  eligible: MarketProduct[],
  _pick: MarketProduct,
  count = 3,
): MarketProduct[] {
  const ranked = [...eligible].sort(
    (a, b) =>
      a.rate - b.rate || a.comparisonRate - b.comparisonRate || a.lender.localeCompare(b.lender),
  );
  const out: MarketProduct[] = [];
  for (const row of ranked) {
    if (out.some((item) => item.lender === row.lender)) continue;
    out.push(row);
    if (out.length >= count) break;
  }
  return out;
}

export type YearPoint = { year: number; extra: number };
export type MonthPoint = { month: number; saved: number };

export function leakByYear(yearlySave: number, years: number): YearPoint[] {
  const n = Math.max(1, Math.round(years));
  const points: YearPoint[] = [];
  for (let year = 0; year <= n; year += 1) {
    points.push({ year, extra: yearlySave * year });
  }
  return points;
}

export function saveByMonth(monthlySave: number, months: number): MonthPoint[] {
  const n = Math.max(1, months);
  const points: MonthPoint[] = [];
  for (let month = 0; month <= n; month += 1) {
    points.push({ month, saved: monthlySave * month });
  }
  return points;
}

export function money(n: number): string {
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  });
  return n < 0 ? `−${formatted}` : formatted;
}

export function moneyExact(n: number): string {
  return n.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

export function pct(n: number): string {
  return `${n.toFixed(2)}%`;
}

export function lvrLabel(maxLvr: number): string {
  return `≤${maxLvr}% LVR`;
}

export function lvrPercent(loan: number, value: number): number | null {
  if (!loan || !value || value <= 0) return null;
  return Math.round((loan / value) * 1000) / 10;
}

export function bandFromPercent(percent: number): LvrBand {
  if (percent <= 60) return "60";
  if (percent <= 80) return "80";
  if (percent <= 90) return "90";
  return "95";
}

export function bandCopy(band: LvrBand): string {
  if (band === "60") return "Under 60%";
  if (band === "80") return "60–80%";
  if (band === "90") return "80–90%";
  return "90%+";
}
